// Why a campaign's provider sync failed, for the publish modal and the action
// banner after it closes.
//
// This replaces `safeCampaignProviderError` in `screens/create.tsx`, a
// browser-side template that turned the stored machine string into a sentence
// of its own ("Meta could not publish this campaign while attempting to …"). It
// said "could not publish" about every failure, including the 2026-10-07 pause
// read-back on a campaign that had been live for days. The words now come from
// the server (`shared::failure_copy`, the `failure` beside `last_error`), the
// same words the publish log, the creative screens and the alert email use;
// this module only picks the block and says what to show when there are none.
//
// A module, not inline in the campaign builder, so every test can reach it.

import type { CampaignSyncStatus, CampaignSyncTarget } from '@/api/manage-types';
import type { FailureView } from '@/api/types.gen';

export interface CampaignPublishFailure {
  /** The server's words, when the block carried them. */
  failure: FailureView | null;
  /**
   * One plain sentence for a single-line surface (the action banner), and the
   * text the notice shows when there are no words. Never the stored machine
   * string: that is the notice's "Technical details", nowhere else.
   */
  text: string;
}

export function providerLabel(target: CampaignSyncTarget): 'Meta' | 'The Trade Desk' {
  return target === 'meta' ? 'Meta' : 'The Trade Desk';
}

export function campaignPublishingFailure(
  status: CampaignSyncStatus | null | undefined,
  target: CampaignSyncTarget,
): CampaignPublishFailure {
  const label = providerLabel(target);
  const expected = status?.[target] ?? null;
  const otherTarget: CampaignSyncTarget = target === 'meta' ? 'ttd' : 'meta';
  if (!expected && status?.[otherTarget]) {
    return {
      failure: null,
      text: `Publishing was queued for ${providerLabel(otherTarget)}, but this campaign targets ${label}. No provider success was assumed.`,
    };
  }
  const failure = expected?.failure ?? null;
  if (failure) {
    return { failure, text: `${failure.headline}. ${failure.next_step}` };
  }
  return {
    failure: null,
    text: expected?.last_error
      ? `${label} could not finish this campaign's sync. The publish log has the details.`
      : `${label} could not finish this campaign's sync. No provider error detail was made available.`,
  };
}
